<template>
  <div v-if="avatars.length">
    <div v-if="compactView" class="typing-message">
      {{
        avatars.length > 1
          ? t('multiple_users_are_typing')
          : `${avatars[0].name} ${t('is_typing_a_message')}`
      }}
    </div>
    <div v-else class="typing-container">
      <div></div>
      <div class="typing-text">
        <FriedH5>{{ typingNames }}</FriedH5>
        <FriedParagraph :size="ParagraphSize.Small">{{ typingText }}</FriedParagraph>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { ParagraphSize } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import type { ChatParticipantId, Conversation, Typing } from '../types';
import { ChatHelper } from '../helpers';

import AvatarHelper from '../../helpers/avatar.helper';

export default defineComponent({
  name: 'ChatTypingStatus',
  props: {
    conversation: {
      type: Object as PropType<Conversation>,
      default: undefined,
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: undefined,
    },
    typing: {
      type: Array as PropType<Array<Typing>>,
      default: () => [],
    },
    compactView: {
      type: Boolean,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const avatars = computed(() =>
      props.typing
        .filter(
          ({ conversationId, participant: chatParticipant }) =>
            conversationId === props.conversation.id &&
            !ChatHelper.isSameParticipant(
              props.currentChatUser,
              chatParticipant,
              chatParticipant.participantId
            ) &&
            (chatParticipant.participant || chatParticipant.user)
        )
        .map(({ participant: chatParticipant }) => {
          if (chatParticipant.participant) {
            return {
              name: AvatarHelper.getDisplayName(chatParticipant.participant),
              thumbUrl: chatParticipant.participant.thumbUrl,
            };
          }
          return {
            name: AvatarHelper.getDisplayName(chatParticipant.user),
            thumbUrl: chatParticipant.user.thumbUrl,
          };
        })
    );

    const typingNames = computed(() => `${avatars.value.map(avatar => avatar.name).join(', ')} `);

    const typingText = computed(() =>
      avatars.value.length === 1 ? t('is_typing_a_message') : t('multiple_users_are_typing')
    );

    return { avatars, t, typingNames, typingText, ParagraphSize };
  },
});
</script>

<style lang="scss" scoped>
.typing-container {
  display: grid;
  grid-template-columns: 2rem 1fr auto;
  column-gap: var(--spacing-75);
  padding: var(--spacing-25) var(--spacing-100);

  .typing-text {
    display: flex;
    gap: var(--spacing-25);

    > h5,
    p {
      color: var(--gray-60);
    }
  }
}

.typing-message {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--gray-39);
}
</style>
